import React, { useMemo, useState } from 'react';
import { Box, Chip, LinearProgress, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Add, EmojiEvents } from '@material-ui/icons';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { ConfirmationDialog } from '../../../../../common/dialogs/ConfirmationDialog';
import { Badge } from '../../../../../common/badges/Badge';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { GET_ADMIN_CONSOLE_ACHIEVEMENTS } from './admin-console-achievements.gql';
import { GetAdminConsoleAchievements } from './__generated__/GetAdminConsoleAchievements';
import { AdminConsoleAchievement } from './__generated__/AdminConsoleAchievement';
import { AchievementEditorDialog } from './AchievementEditorDialog';
import { AchievementItemMenuButton } from './AchievementItemMenu';
import { BackfillAchievementDialog } from './BackfillAchievementDialog';
import { useSaveAchievement } from './useSaveAchievement';
import {
  AchievementDraft,
  AchievementMetric,
  METRICS,
  NumericReportField,
  draftFromAchievement,
  emptyDraft,
  parseBadgeDesign,
} from './achievement-model';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      paddingBottom: 40,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
      [theme.breakpoints.down('sm')]: { paddingLeft: 16, paddingRight: 16 },
    },
    heading: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '16px',
      paddingBottom: 8,
      borderBottom: '1px solid #efefef',
    },
    headingTitle: { color: theme.color.grey.neutralBrand900, fontWeight: 600, fontSize: 20, paddingBottom: 5 },
    panel: {
      background: 'white',
      padding: '1.25rem 1.875rem',
      marginTop: '2.25rem',
      [theme.breakpoints.down('sm')]: { padding: '1rem' },
    },
    sectionTitle: { fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center' },
    sectionDescription: { fontSize: 14, color: '#808080' },
    row: { borderTop: '1px solid #EFEFEF', padding: '.6rem 1rem .6rem 0', marginTop: '1.25rem' },
    rowHead: { display: 'flex', alignItems: 'center', gap: '12px' },
    stack: { display: 'flex', alignItems: 'center', '& > * + *': { marginLeft: '-14px' } },
    name: { fontSize: 15, fontWeight: 600, flex: 1, minWidth: 0 },
    description: { fontSize: 14, color: '#808080', margin: '4px 0 8px' },
    chips: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
    chip: { borderRadius: '4px', fontSize: '11px', fontWeight: 500, color: '#333' },
  })
);

const toReportFields = (data?: GetAdminConsoleAchievements): NumericReportField[] => {
  if (!data) return [];
  return data.vm.reportTypes
    .filter((reportType) => !reportType.dateDeactivated)
    .flatMap((reportType) =>
      reportType.items.flatMap((item) =>
        item.__typename === 'VOLUNTEER_ReportTypeFieldType' &&
        item.dateAdded &&
        !item.dateRemoved &&
        item.field.__typename === 'VOLUNTEER_NumericFieldType'
          ? [{ fieldId: item.field.fieldId, name: item.field.name, reportTypeName: reportType.name }]
          : []
      )
    );
};

export const AdminConsoleAchievements = () => {
  const classes = useStyles();
  const { isSm } = useCampfireTheme();
  const { setSnackbar } = useSnackbar();
  const { data, loading, refetch } = useCampfireQuery<GetAdminConsoleAchievements, {}>(GET_ADMIN_CONSOLE_ACHIEVEMENTS);
  const { save, saving } = useSaveAchievement();
  const deleteFetch = useEndpointFetch<{ achievementId: string }>('/vm/admin-console/achievement/delete');
  const [editorDraft, setEditorDraft] = useState<AchievementDraft>();
  const [deleting, setDeleting] = useState<AdminConsoleAchievement>();
  const [backfilling, setBackfilling] = useState<AdminConsoleAchievement>();
  const backfillFetch = useEndpointFetch<{ achievementId: string }>('/vm/admin-console/achievement/backfill');

  const achievements = data?.vm.achievements ?? [];
  const reportFields = useMemo(() => toReportFields(data), [data]);
  const certificateTemplates = data?.vm.certificateTemplates ?? [];

  const reload = () => {
    if (refetch) setTimeout(refetch, 0);
  };

  const onSave = async (draft: AchievementDraft, closeEditor = true) => {
    const result = await save(draft);
    if (!result.ok) {
      setSnackbar({ open: true, variant: 'error', message: result.message ?? 'Unable to save achievement' });
      reload();
      return;
    }
    setSnackbar({ open: true, variant: 'success', message: `${draft.name} saved` });
    if (closeEditor) setEditorDraft(undefined);
    reload();
  };

  const onAction = (achievement: AdminConsoleAchievement, action: string) => {
    if (action === 'edit') setEditorDraft(draftFromAchievement(achievement));
    if (action === 'toggle') onSave({ ...draftFromAchievement(achievement), isEnabled: !achievement.isEnabled }, false);
    if (action === 'delete') setDeleting(achievement);
    if (action === 'backfill') setBackfilling(achievement);
  };

  const onDeleteConfirm = () => {
    if (!deleting) return;
    const { name, achievementId } = deleting;
    setDeleting(undefined);
    deleteFetch
      .run({ achievementId })
      .then((response) => {
        if (!response.ok) throw new Error();
        setSnackbar({ open: true, variant: 'success', message: `${name} deleted` });
        reload();
      })
      .catch(() => setSnackbar({ open: true, variant: 'error', message: 'Unable to delete achievement' }));
  };

  const onBackfillConfirm = () => {
    if (!backfilling) return;
    const { name, achievementId } = backfilling;
    setBackfilling(undefined);
    backfillFetch
      .run({ achievementId })
      .then((response) => {
        if (!response.ok) throw new Error(response.data?.message);
        setSnackbar({ open: true, variant: 'success', message: `Backfill started for ${name}` });
      })
      .catch((e) => setSnackbar({ open: true, variant: 'error', message: e?.message || 'Unable to start backfill' }));
  };

  const reportFieldName = (achievement: AdminConsoleAchievement) =>
    reportFields.find((field) => field.fieldId === achievement.reportFieldId)?.name ?? 'Report field';

  return (
    <Box className={classes.container}>
      <Box
        className={classes.heading}
        flexDirection={isSm ? 'column' : 'row'}
        alignItems={isSm ? 'flex-start' : 'center'}
      >
        <Box>
          <Typography variant='h2' className={classes.headingTitle}>
            Achievements
          </Typography>
          <Typography variant='body2'>
            Recognise volunteers automatically as they reach milestones, or award levels manually
          </Typography>
        </Box>
        <ConsoleActionButton
          endIcon={<Add />}
          variant='contained'
          color='primary'
          onClick={() => setEditorDraft(emptyDraft())}
        >
          Add Achievement
        </ConsoleActionButton>
      </Box>

      {loading && <LinearProgress style={{ marginTop: '1rem' }} />}

      {!loading && achievements.length === 0 ? (
        <Box className={classes.panel}>
          <Typography className={classes.sectionTitle}>
            <EmojiEvents style={{ fontSize: 14, paddingRight: 5 }} /> Create your first achievement
          </Typography>
          <Typography variant='body2' className={classes.sectionDescription}>
            {
              "You haven't created any achievements yet. Click the 'Add Achievement' button top right to add your first."
            }
          </Typography>
        </Box>
      ) : (
        <Box className={classes.panel}>
          <Typography className={classes.sectionTitle}>
            <EmojiEvents style={{ fontSize: 14, paddingRight: 5 }} /> Achievements
          </Typography>
          <Typography variant='body2' className={classes.sectionDescription}>
            Each achievement has levels. A level can award a badge, a certificate, or both.
          </Typography>

          {achievements.map((achievement) => {
            const metric = METRICS[achievement.metric as AchievementMetric];
            const levelCount = achievement.levels.length;
            return (
              <Box key={achievement.achievementId} className={classes.row}>
                <Box className={classes.rowHead}>
                  <Box className={classes.stack} style={{ opacity: achievement.isEnabled ? 1 : 0.5 }}>
                    {achievement.levels.map((level) => {
                      const design = parseBadgeDesign(level.badgeDesign);
                      return design ? (
                        <Badge key={level.achievementLevelId} design={design} size={36} label={level.name} />
                      ) : null;
                    })}
                  </Box>
                  <Typography className={classes.name} style={{ opacity: achievement.isEnabled ? 1 : 0.5 }}>
                    {achievement.name}
                  </Typography>
                  <AchievementItemMenuButton
                    isEnabled={achievement.isEnabled}
                    canBackfill={achievement.isEnabled && achievement.metric !== 'manual'}
                    onAction={(action) => onAction(achievement, action)}
                  />
                </Box>
                {achievement.description && (
                  <Typography className={classes.description}>{achievement.description}</Typography>
                )}
                <Box className={classes.chips} mt={achievement.description ? 0 : 1}>
                  <Chip
                    className={classes.chip}
                    label={achievement.metric === 'report_field_total' ? reportFieldName(achievement) : metric.label}
                  />
                  <Chip className={classes.chip} label={`${levelCount} level${levelCount === 1 ? '' : 's'}`} />
                  <Chip
                    className={classes.chip}
                    style={achievement.isEnabled ? { color: '#fff', backgroundColor: '#48976C' } : undefined}
                    label={achievement.isEnabled ? 'Enabled' : 'Disabled'}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {editorDraft && (
        <AchievementEditorDialog
          open
          initialDraft={editorDraft}
          reportFields={reportFields}
          certificateTemplates={certificateTemplates}
          saving={saving}
          onClose={() => setEditorDraft(undefined)}
          onSave={(draft) => onSave(draft)}
        />
      )}

      <BackfillAchievementDialog
        achievement={backfilling}
        onClose={() => setBackfilling(undefined)}
        onConfirm={onBackfillConfirm}
      />

      <ConfirmationDialog
        title='Delete achievement'
        body={`Delete "${deleting?.name ?? ''}"? It stops being awarded and is hidden from volunteer profiles.`}
        open={Boolean(deleting)}
        closeActionText='Cancel'
        confirmActionText='Delete'
        handleCloseClick={() => setDeleting(undefined)}
        handleConfirmClick={onDeleteConfirm}
      />
    </Box>
  );
};
